import React, { useEffect, useRef } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { NavLink } from "react-router-dom";
import {
  Box,
  Text,
  Flex,
  ModalBody,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import {
  USER_GRADES_SUBJECTS,
  USER_DYNOES,
  DYNOSTATUSES,
} from "../../graphql/queries";
import { FormSelect } from "../../components/FormSelect";
import { MyDynoTabsComponent } from "../../components/MyDynoTabsComponent";
import { DomainComponent } from "../../components/DomainComponent";
import { UPDATE_DYNO } from "../../graphql/mutations";
import { ModalBox } from "../../components/ModalBox";
import { FormInput } from "../../components/FormInput";
import { AddToDynoClass } from "../../components/AddToDynoClass";
import { AddOrgModal } from "../../components/AddOrgModal";
import { AlertDialog } from "../../components/AlertDialog";
import { combinedStore } from "../../store";

const statusTypes = {
  Draft: "In progress",
  Published: "Ready to share",
  AddedToClass: "Added to class",
};

export const MyDynosPage = ({ user, userRole }) => {
  const {
    userGrades,
    selectedGrade,
    subjectWiseDynos,
    isWorking,
    selectedDomain,
    domain,
    dynoStatuses,
    isDynoTitleUpdating,
    selectedSubjectName,
    selectedGradeName,
    addToClassStatus,
    domainName,
    dynoTitle,
    updateState,
    setIsWorking,
  } = combinedStore((state) => state);
  const dynoIdRef = useRef("");
  const dynoToAddIdRef = useRef("");
  const isUpdatingDynoRef = useRef(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: dynoModalIsOpen,
    onOpen: dynoModalOnOpen,
    onClose: dynoModalOnClose,
  } = useDisclosure();
  const {
    isOpen: addOrgModalIsOpen,
    onOpen: addOrgModalOnOpen,
    onClose: addOrgModalOnClose,
  } = useDisclosure();
  const {
    isOpen: alertIsOpen,
    onOpen: alertOnOpen,
    onClose: alertOnClose,
  } = useDisclosure();

  const [getDynoStatuses] = useLazyQuery(DYNOSTATUSES, {
    onCompleted: (data) => {
      updateState({
        dynoStatuses: data.dynoStatuses
          .filter(
            (ds) => ds.statusType !== "Test" && ds.statusType !== "AddedToClass"
          )
          .map((ds) => ({ ...ds, name: statusTypes[ds.statusType] })),
        addToClassStatus: data.dynoStatuses.find(
          (ds) => ds.statusType === "AddedToClass"
        ),
      });
    },
    onError: () => {
      setIsWorking(false);
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const [getUserGradesSubjects] = useLazyQuery(USER_GRADES_SUBJECTS, {
    onCompleted: async (data) => {
      if (
        data?.getDynosPlusUserGradesAndSubjects?.grades &&
        data?.getDynosPlusUserGradesAndSubjects?.grades.length > 0
      ) {
        const grades = [...data.getDynosPlusUserGradesAndSubjects.grades];
        const sortedGrades = grades.sort((a, b) => {
          const nameA = parseInt(a.name);
          const nameB = parseInt(b.name);

          return nameA - nameB;
        });
        updateState({
          userGrades: sortedGrades,
          selectedGrade: sortedGrades[0].id,
          selectedGradeName: sortedGrades[0].name,
        });
        getUserDynoes({
          variables: {
            where: {
              id: sortedGrades[0].id,
            },
          },
        });
      } else {
        setIsWorking(false);
      }
    },
    onError: () => {
      setIsWorking(false);
    },
    fetchPolicy: "cache-and-network",
  });

  const [getUserDynoes] = useLazyQuery(USER_DYNOES, {
    onCompleted: async (data) => {
      if (!isUpdatingDynoRef.current) {
        const subjects = data.getDynosPlusUserDynos;
        updateState({
          isWorking: false,
          selectedDomain: subjects[0].domains[0].id,
          domainName: subjects[0].domains[0].name,
          selectedSubjectName:
            subjects[0]?.name === "Maths" ? "Math" : subjects[0]?.name,
          domain: {
            ...subjects[0].domains[0],
            topics: subjects[0].domains[0].topics.map((t) => ({
              ...t,
              subTopics: t.subTopics.map((st) => ({
                ...st,
                dynoes: st.dynoes.map((d) => ({ ...d, isWorking: false })),
              })),
            })),
          },
          subjectWiseDynos: subjects,
        });
      }
    },
    onError: () => {
      setIsWorking(false);
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const [updateDyno] = useMutation(UPDATE_DYNO, {
    update: (cache, { data }) => {
      updateDomain(
        data.updateDyno.id,
        data.updateDyno.status,
        data.updateDyno.dynoTitle,
        false,
        {
          isDynoTitleUpdating: false,
          isWorking: false,
          dynoTitle: "",
        }
      );
      onClose();
      setTimeout(() => {
        isUpdatingDynoRef.current = false;
      }, 500);
    },
    onError: () => {
      updateDomain(null, null, null, false, {
        isDynoTitleUpdating: false,
        isWorking: false,
        dynoTitle: "",
      });
      onClose();
      setTimeout(() => {
        isUpdatingDynoRef.current = false;
      }, 500);
    },
  });

  useEffect(() => {
    getUserGradesSubjects();
    getDynoStatuses();
  }, [getUserGradesSubjects, getDynoStatuses]);

  const updateDomain = (
    id,
    status,
    dynoTitle,
    isWorking = false,
    otherStates
  ) => {
    let domainCopy = { ...domain };
    const topicIndex = domainCopy.topics.findIndex((topic) =>
      topic.subTopics.find((st) =>
        st.dynoes.find((dyno) => (id ? dyno.id === id : dyno.isWorking))
      )
    );
    const subTopicIndex = domainCopy.topics[topicIndex].subTopics.findIndex(
      (st) => st.dynoes.find((dyno) => (id ? dyno.id === id : dyno.isWorking))
    );
    const dynoIndex = domainCopy.topics[topicIndex].subTopics[
      subTopicIndex
    ].dynoes.findIndex((dyno) => (id ? dyno.id === id : dyno.isWorking));
    domainCopy.topics[topicIndex].subTopics[subTopicIndex].dynoes[
      dynoIndex
    ].isWorking = false;
    domainCopy.topics[topicIndex].subTopics[subTopicIndex].dynoes[dynoIndex] = {
      ...domainCopy.topics[topicIndex].subTopics[subTopicIndex].dynoes[
        dynoIndex
      ],
      isWorking: isWorking,
      ...(dynoTitle ? { dynoTitle: dynoTitle } : {}),
      ...(status ? { status: status } : {}),
    };
    updateState({
      domain: domainCopy,
      ...otherStates,
    });
  };

  const onSelectGrade = (e) => {
    if (e.target.value !== "Select grade") {
      updateState({
        isWorking: true,
        selectedGrade: e.target.value,
        selectedGradeName: userGrades.find((ug) => ug.id === e.target.value)
          ?.name,
      });
      getUserDynoes({
        variables: {
          where: {
            id: e.target.value,
          },
        },
      });
    }
  };

  const onSelectDomain = (e) => {
    if (e.target.value !== "Select domain") {
      const subject = subjectWiseDynos.find((s) =>
        s.domains.find((d) => d.id === e.target.value)
      );
      const selected_domain = subject?.domains?.find(
        (d) => d.id === e.target.value
      );
      updateState({
        selectedDomain: selected_domain.id,
        domain: {
          ...selected_domain,
          topics: selected_domain.topics.map((t) => ({
            ...t,
            subTopics: t.subTopics.map((st) => ({
              ...st,
              dynoes: st.dynoes.map((d) => ({ ...d, isWorking: false })),
            })),
          })),
        },
        domainName: selected_domain.name,
      });
    }
  };

  const onDynoStatusChange = (statusId, id) => {
    isUpdatingDynoRef.current = true;
    updateDomain(id, null, null, true, {});
    updateDyno({
      variables: {
        where: {
          id: id,
        },
        data: {
          status: {
            connect: {
              id: statusId,
            },
          },
        },
      },
    });
  };

  const editDynoTitle = (id, dynoTitle) => {
    dynoIdRef.current = id;
    updateState({
      dynoTitle,
    });
    onOpen();
  };

  const updateDynoTitle = (e) => {
    e.preventDefault();
    updateState({
      isDynoTitleUpdating: true,
    });
    isUpdatingDynoRef.current = true;
    updateDyno({
      variables: {
        where: {
          id: dynoIdRef.current,
        },
        data: {
          dynoTitle: {
            set: dynoTitle,
          },
        },
      },
    });
  };

  const addDynoToClass = (id) => {
    if (
      (userRole === "Teacher" && user?.organizations?.length > 0) ||
      userRole === "Guardian"
    ) {
      dynoToAddIdRef.current = id;
      dynoModalOnOpen();
    } else {
      alertOnOpen();
    }
  };

  const updateDynoStatus = () => {
    isUpdatingDynoRef.current = true;
    updateDyno({
      variables: {
        where: {
          id: dynoToAddIdRef.current,
        },
        data: {
          status: {
            connect: {
              id: addToClassStatus.id,
            },
          },
        },
      },
    });
  };

  return (
    <Box
      style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "30px 20px",
      }}
    >
      <FormSelect
        formProps={{ isRequired: true, style: { maxWidth: "200px" } }}
        padding={{ base: "0 10px 10px 0", md: "0 20px 30px 0" }}
        label="Grade"
        options={userGrades.map((grade) => ({
          ...grade,
          value: grade.id,
        }))}
        value={selectedGrade}
        disabled={userGrades.length === 0}
        placeholder="Select grade"
        selectOnChange={onSelectGrade}
        name="grade"
        isWorking={isWorking}
      />
      {subjectWiseDynos.length > 0 && (
        <MyDynoTabsComponent
          tabs={subjectWiseDynos.map((sd) => ({
            id: sd.id,
            name: sd.name,
            children: (
              <>
                <Box>
                  <FormSelect
                    formProps={{ isRequired: false }}
                    padding={{ base: "0 10px 10px 0", md: "0 20px 30px 0" }}
                    label="Domain"
                    options={sd.domains.map((domain) => ({
                      ...domain,
                      value: domain.id,
                    }))}
                    value={selectedDomain}
                    disabled={userGrades.length === 0}
                    placeholder="Select domain"
                    selectOnChange={onSelectDomain}
                    name="domain"
                    isWorking={isWorking}
                  />
                  <DomainComponent
                    domain={domain}
                    dynoStatuses={dynoStatuses}
                    onDynoStatusChange={onDynoStatusChange}
                    addDynoToClass={addDynoToClass}
                    editDynoTitle={editDynoTitle}
                  />
                </Box>
              </>
            ),
          }))}
        />
      )}
      {subjectWiseDynos.length === 0 &&
        userGrades.length === 0 &&
        !isWorking && (
          <Flex
            style={{
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              fontSize: "20px",
            }}
          >
            <Text>
              Start creating dynos{" "}
              <NavLink
                to="/createdyno"
                style={{ color: "rgb(29 114 201)", cursor: "pointer" }}
              >
                create new dyno
              </NavLink>
            </Text>
          </Flex>
        )}
      <ModalBox
        title="Update dyno title"
        isOpen={isOpen}
        modalProps={{
          closeOnOverlayClick: false,
        }}
        onClose={onClose}
      >
        <ModalBody>
          <form onSubmit={updateDynoTitle}>
            <FormInput
              label="Dyno title"
              name="dynoTitle"
              type="text"
              placeholder=""
              inputOnChange={(e) => {
                updateState({
                  dynoTitle: e.target.value,
                });
              }}
              value={dynoTitle}
              formProps={{ isRequired: true, isDisabled: isDynoTitleUpdating }}
              padding="0 0 20px"
            />
            <Flex
              style={{
                justifyContent: "flex-end",
                margin: "0 0 16px",
              }}
            >
              <Button
                type="submit"
                isLoading={isDynoTitleUpdating}
                loadingText="Updating"
                colorScheme="brand"
                _hover={{
                  bg: "blue.500",
                }}
              >
                Update
              </Button>
            </Flex>
          </form>
        </ModalBody>
      </ModalBox>
      <AddToDynoClass
        isOpen={dynoModalIsOpen}
        onClose={dynoModalOnClose}
        user={user}
        dynoToAdd={dynoToAddIdRef.current}
        selectedGradeName={selectedGradeName}
        selectedSubjectName={selectedSubjectName}
        domainName={domainName}
        updateDynoStatus={updateDynoStatus}
      />
      <AlertDialog
        title="Info"
        description="You haven't added any organization yet. Please add to create classes and adding students."
        actionButtonText="Add school or organization"
        isDeleteButton={false}
        isOpen={alertIsOpen}
        onCancel={alertOnClose}
        onDelete={() => {
          alertOnClose();
          addOrgModalOnOpen();
        }}
        isWorking={false}
        loadingText=""
      />
      <AddOrgModal
        user={user}
        isTeacher={userRole === "Teacher"}
        isOpen={addOrgModalIsOpen}
        onClose={addOrgModalOnClose}
      />
    </Box>
  );
};
